/**
 * @file AI Gallery 面板 — 主体内容区：iframe 预览 / 代码高亮展示
 * 
 * 代码区域使用深色主题 AceEditor 展示 HTML 代码
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useIntl } from 'umi';
import AceEditor from 'react-ace';
import classNames from 'classnames';
import Loading from '@home/components/Loading';
import 'ace-builds/src-noconflict/mode-html';
import 'ace-builds/src-noconflict/theme-monokai';
import { injectIframeScrollbarStyle } from '../../utils/injectIframeScrollbarStyle';

export interface IAiGalleryContentAreaProps {
  /** 是否为预览 Tab */
  isPreviewStep: boolean;
  /** iframe 预览用 URL（签名后的 COS URL） */
  previewUrl?: string;
  /** HTML 源码字符串 */
  htmlSource?: string;
  /** 是否正在加载 */
  isLoading?: boolean;
}

/**
 * AI Gallery 中部内容区域（预览与代码 Tab 切换）
 */
export const AiGalleryContentArea: React.FC<IAiGalleryContentAreaProps> = ({
  isPreviewStep,
  previewUrl,
  htmlSource,
  isLoading = false,
}) => {
  const { formatMessage } = useIntl();
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const [iframeError, setIframeError] = useState(false);
  const [iframeLoading, setIframeLoading] = useState(true);

  /** iframe 加载完成：关 loading，注入与 Gallery Report 相同的滚动条 */
  const handleIframeLoad = useCallback(() => {
    setIframeLoading(false);
    setIframeError(false);
    injectIframeScrollbarStyle(iframeRef.current);
  }, []);

  /** iframe 加载失败 */
  const handleIframeError = useCallback(() => {
    setIframeLoading(false);
    setIframeError(true);
  }, []);

  /** 预览 URL 变化时重置状态 */
  useEffect(() => {
    if (previewUrl) {
      setIframeLoading(true);
      setIframeError(false);
    }
  }, [previewUrl]);

  /** 
   * 内容下载完成后，设置 iframe 渲染超时
   * 等内容下载完成后，再等待 1.5 秒让 iframe 渲染，然后强制隐藏 Loading
   */
  useEffect(() => {
    // 只有当内容下载完成（isLoading=false）且有预览 URL 且 iframe 仍在 loading 时才设置超时
    if (!isLoading && previewUrl && iframeLoading) {
      const timeoutId = setTimeout(() => {
        setIframeLoading(false);
        injectIframeScrollbarStyle(iframeRef.current);
      }, 1500);
      
      return () => clearTimeout(timeoutId);
    }
  }, [isLoading, previewUrl, iframeLoading]);

  const handleEditorLoad = useCallback((editor: any) => {
    // 禁用所有编辑相关的命令
    editor.commands.removeCommands(['insertstring', 'backspace', 'del', 'enter']);
    // 移除只读警告提示
    const cursorElement = editor.renderer.$cursorLayer.element;
    cursorElement.style.display = 'none';
    // 禁用文本输入
    const textInput = editor.textInput.getElement();
    textInput.disabled = true;
    // 移除可能触发提示的事件监听器
    editor.off('change');
    editor.off('changeSelection');
  }, []);

  /** 预览区域内容 */
  const renderPreviewContent = () => {
    // 加载中
    if (isLoading) {
      return (
        <div className="dba-ai-gallery-panel__preview-placeholder">
          <Loading text={formatMessage({ id: 'dba_loading' })} />
        </div>
      );
    }

    // 无预览内容
    if (!previewUrl) {
      return (
        <div
          className="dba-ai-gallery-panel__preview-placeholder"
          aria-hidden={true}
        >
          {formatMessage({ id: 'dba_ai_gallery_preview_placeholder' })}
        </div>
      );
    }

    // iframe 加载错误
    if (iframeError) {
      return (
        <div className="dba-ai-gallery-panel__preview-placeholder">
          <span>{formatMessage({ id: 'dba_ai_gallery_preview_error' })}</span>
        </div>
      );
    }

    return (
      <>
        {iframeLoading && (
          <div className="dba-ai-gallery-panel__preview-loading">
            <Loading text={formatMessage({ id: 'dba_loading' })} />
          </div>
        )}
        <div className="dba-ai-gallery-panel__iframe-wrapper">
          <iframe
            ref={iframeRef}
            className={classNames('dba-ai-gallery-panel__iframe', {
              'dba-ai-gallery-panel__iframe--loading': iframeLoading,
            })}
            src={previewUrl}
            sandbox="allow-scripts allow-same-origin allow-popups allow-forms allow-downloads"
            title="AI Gallery Preview"
            onLoad={handleIframeLoad}
            onError={handleIframeError}
          />
        </div>
      </>
    );
  };

  /** 代码区域内容 - 深色主题 */
  const renderCodeContent = () => {
    // 加载中
    if (isLoading) {
      return (
        <div className="dba-ai-gallery-panel__code-placeholder">
          <Loading text={formatMessage({ id: 'dba_loading' })} />
        </div>
      );
    }

    // 使用深色主题 AceEditor 展示代码（有无源码都显示编辑器）
    return (
      <div className="dba-ai-gallery-panel__code-editor">
        <AceEditor
          mode="html"
          value={htmlSource || ''}
          width="100%"
          height="100%"
          theme="monokai"
          fontSize={14}
          readOnly
          showPrintMargin={false}
          showGutter
          wrapEnabled={!!htmlSource}
          highlightActiveLine={false}
          onLoad={handleEditorLoad}
          setOptions={{
            useWorker: false,
            readOnly: true,
            showLineNumbers: true,
            tabSize: 2,
            showFoldWidgets: false,
          }}
          editorProps={{ $blockScrolling: true }}
        />
      </div>
    );
  };

  return (
    <div className="dba-ai-gallery-panel__content">
      {isPreviewStep && (
        <div className="dba-ai-gallery-panel__preview">
          {renderPreviewContent()}
        </div>
      )}

      {!isPreviewStep && (
        <div className="dba-ai-gallery-panel__code-pane">
          {renderCodeContent()}
        </div>
      )}
    </div>
  );
};

export default AiGalleryContentArea;
